/* What the Optimizer would move, and why.
 *
 * Online Booking only sees the book as written. If Helen is the one facial tech in
 * today and she has pedicures anybody could do from noon to two, a client looking
 * online sees no facial until two, though the salon would seat them at noon. This
 * works out the technician changes that open that time up, for the desk to approve.
 * The design is docs/superpowers/specs/2026-11-01-optimizer-module-design.md.
 *
 * It only ever proposes. Nothing here changes a booking, and a proposal may be minutes
 * old by the time it is tapped, so recheckMove looks again before each save.
 *
 *
 * THE DAY
 *
 * `day` is what `buildDay` in the optimizer function's recheck.js returns, so a day
 * read for a re-check can be planned from directly, plus the additions marked NEW,
 * which the scheduled check has to supply:
 *
 *   date, isToday, now   salon-local date, whether the board counts, wall-clock now
 *   appointments         the proxy's calendar: { id, starts_at, cancellation_type,
 *                        workflow_status, sale_id, client: { id, first_name, last_name },
 *                        parts: [{ service_id, service, staff_id, staff, starts_at, ends_at,
 *                        duration_minutes, staff_requested_type, additional_staff_id,
 *                        additional_staff_requested_type, service_option_ids }] }
 *     NEW  repeating       true on an appointment in a repeating series
 *     NEW  can_modify      false when Mangomint says it can't be modified
 *     NEW  has_add_ons     true on a part with add-ons or customizations
 *   staff                { mangomintId: { id, name, active, serviceIds, ownClientsOnly } }
 *     NEW  availableInOnlineBooking   only a literal true counts
 *   shifts, blocks       { mangomintId: [{ from, to }] }, blocks with a `label`
 *   serviceMap, services, optionRules   as buildDay makes them
 *   board                today: { mangomintId: { present, skipped, reason, outAt } }
 *                        any other day: null
 *   squares              today: { mangomintId: [{ from, to, appointmentId }] }
 *   NEW  tz              the salon's time zone. Without it, wall clock is taken as real.
 *   NEW  notes           { clientId: the text of their notes on file }
 *
 * The three appointment flags are not in the proxy's calendar answer yet. Until they
 * are, such an appointment is proposed and the proxy refuses it at the save with
 * 409 appointment_not_editable, which is safe but a wasted line.
 *
 * Notes change only how a line is shown, never who receives it, so a run can plan,
 * read the notes for the clients on its lines, and plan again with the same seed to
 * get the same moves with the notes on them.
 *
 *   priority   { id, label, kind: 'service' | 'category',
 *                services: [{ id: mangomintServiceId, minutes, availableInOnlineBooking }] }
 *
 * A service that is not bookable online is not a priority at all: online availability
 * is the whole gain. A priority with none left is ignored.
 *
 *
 * TIME
 *
 * Times are milliseconds of the salon's wall clock, labelled UTC, the same as recheck.js.
 * Who overlaps whom can be read straight off that. How LONG a gap is cannot, because the
 * night the clocks change has a 25-hour or 23-hour wall. So gaps are worked out on a
 * real timeline (see `timeline`) and answers come back as wall clock.
 *
 *
 * WHAT IT DECIDES
 *
 * Scarce: a priority with 1 or 2 staff who are on shift that day, offered online, able
 * to do at least one of its services, not on own clients only, and for today on the
 * board. Someone on a break is still in; someone sent home is not. With 3 or more in,
 * or nobody, it says nothing.
 *
 * One block per scarce technician and priority. It takes everything on her book that
 * may move, offers each to a colleague in time order, and keeps those moves that sit
 * in a free stretch long enough for the priority's full duration (for a category, the
 * shortest of its services that she can do, since a gap for one she can't do opens
 * nothing). It clears as much of her day as it can, not just the earliest slot. A
 * booking whose move would open nothing usable stays where it is.
 *
 * It moves one service, not its visit (Kevin). Half of a pair on one technician can
 * still move, and the line says so, because that can split one square into two.
 *
 * No chains: a move lands only in open time on the receiver's book as written. Nobody
 * else's booking is ever moved to make room, and nobody scarce for anything receives
 * anything that day, so a booking can't go round in a circle.
 *
 * Promises: each receiver is promised to one booking at a time. `promised` is a list of
 * { id, staffId, from, to } shared across blocks and days. A receiver can have a noon
 * and a two o'clock promised, but not two noons, and no booking is promised twice.
 * Planning adds to the list it is given. A technician scarce for two priorities gets
 * her moves in whichever block comes first, and the other appears only if it can
 * gain something without them.
 *
 *
 * TIES AND STABILITY
 *
 * Ties are which scarce technician goes first and which equally suitable colleague
 * receives. All of it comes from `seed` through a hash, one draw per question rather
 * than a generator running in sequence, so a booking's pick does not shift because
 * some other booking came or went. A colleague already taking the other half of this
 * visit is more suitable than one who is not, because it keeps the square whole.
 *
 * Same day, same seed: byte-identical output, whatever order the book arrives in. There
 * is no clock in here and no unseeded randomness; `now` is handed in.
 *
 * The scheduled check's base seed is fixed per salon. `stableKey` is a hash of the
 * bookings on a block, not the seed and not the picks, and names the block's stored row.
 *
 * A dismissal is per booking, not per block. A block's lines change for reasons that
 * have nothing to do with the desk's answer: a booking passes the cut-off, a receiver
 * fills up, another block takes the colleague. A dismissal tied to the block would lapse
 * each time and bring the rest back. So `dismissed` is a list of `dismissalKeys`, one
 * per booking, each a hash of that booking as it stood. A dismissed booking stays
 * where it is and promises nobody; a block appears only if something else remains. A
 * booking that changes has a new hash, and can be proposed again.
 *
 *
 * LINES
 *
 *   { id, kind: 'move' | 'locked' | 'uneditable', ticked, to, why, warn, notes,
 *     appointment_id, service_id, starts_at, from_staff_id, to_staff_id,
 *     service, clientName, staffName, toName, fromMs, toMs }
 *
 * A block's `gain` counts every move on it, ticked or not. `gainFor(block, tickedIds)` is
 * the same measure for only the ticked ones, from the block's own `basis`, which stays
 * plain JSON so a stored block can answer too.
 *
 * `id` and the five wire fields are what parseMoves in plan.js takes. `to` and
 * `to_staff_id` are the receiver, null on a line that stays. `warn` is the technicians
 * a note names, and a move with any starts unticked with the note. A move with any
 * other note shows it, ticked, because name matching can't see "Prefers female staff".
 * Locked and uneditable lines appear, unticked and with the reason, only where they
 * touch the stretch being cleared: they say why time stays blocked there, and a request
 * at 5 PM says nothing about clearing noon.
 *
 * Pure: arguments in, plain objects out, and no store, clock or DOM. The one thing done
 * to an argument is adding to `promised`.
 */
import { firstGap, mayTake } from './swap.js';
import { notesMention } from './client-notes.js';
import {
  allowedStaffFor, instantToWall, mappedServiceFor, overlaps, partSpan, requested, spansFor,
} from './day-read.js';

var DAY_MS = 86400000;
var MINUTE = 60000;
// Only these are clients not here yet, the same list recheckMove's optimizer source uses.
var MOVABLE = ['Unconfirmed', 'Confirmed'];

/* ---- Small helpers ---- */

function hash(text) {
  var h = 2166136261;
  for (var i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // FNV alone leaves the low bits weak for short, similar strings. This mixes them.
  h ^= h >>> 16; h = Math.imul(h, 0x85ebca6b);
  h ^= h >>> 13; h = Math.imul(h, 0xc2b2ae35);
  h ^= h >>> 16;
  return h >>> 0;
}

function hex(n) { return ('0000000' + n.toString(16)).slice(-8); }

function staffIdsOf(day) {
  return Object.keys(day.staff).map(Number).sort(function (a, b) { return a - b; });
}

function nameOf(day, staffId, fallback) {
  var s = day.staff[staffId];
  return s ? s.name : (fallback || 'That technician');
}

function compareStrings(a, b) { return a < b ? -1 : (a > b ? 1 : 0); }

/* The real timeline for a day. `real` turns wall clock into a real instant and `wall`
 * turns it back. A wall time that happens twice, the night clocks go back, is its first
 * occurrence; one that never happens, the night they go forward, reads as if the
 * clocks had not changed yet. No zone given means wall clock is taken as real. */
function timeline(tz) {
  function same(x) { return x; }
  if (!tz) return { real: same, wall: same };
  function offset(instant) { return instantToWall(new Date(instant), tz) - instant; }
  return {
    real: function (w) {
      if (!Number.isFinite(w)) return w;
      var before = offset(w - DAY_MS), after = offset(w + DAY_MS);
      if (instantToWall(new Date(w - before), tz) === w) return w - before;
      if (instantToWall(new Date(w - after), tz) === w) return w - after;
      return w - before;
    },
    wall: function (r) { return Number.isFinite(r) ? r + offset(r) : r; },
  };
}

function usablePriorities(priorities) {
  var out = [];
  (priorities || []).forEach(function (p) {
    var services = (p.services || []).filter(function (s) { return s.availableInOnlineBooking === true; });
    if (services.length) out.push(Object.assign({}, p, { services: services }));
  });
  return out;
}

function isOnPriority(part, priorities) {
  return priorities.some(function (p) {
    return p.services.some(function (s) { return s.id === part.service_id; });
  });
}

/* Is this booked service one of the priorities? Only a service bookable online counts,
 * so the planner and recheckMove agree. */
export function isPriorityPart(part, priorities) {
  return isOnPriority(part, usablePriorities(priorities));
}

function servicesCanDo(staff, priority, day) {
  return priority.services.filter(function (s) {
    return mayTake(staff, day.serviceMap[s.id] || null, null);
  });
}

function countsAsIn(staffId, day) {
  var s = day.staff[staffId];
  if (!s.active || s.availableInOnlineBooking !== true || s.ownClientsOnly) return false;
  if (!spansFor(day, staffId).length) return false;
  if (!day.isToday) return true;
  var row = day.board && day.board[staffId];
  return !!row && row.present && !(row.skipped && row.reason === 'done');
}

/* ---- The public questions ---- */

/* `days + 1` salon-local dates from `todayLocal`. The date is counted on its own, not
 * by adding 24 hours to a clock, so a 25-hour day can't repeat or skip one. */
export function horizonDates(todayLocal, days) {
  var parts = String(todayLocal).split('-').map(Number);
  var out = [];
  for (var i = 0; i <= days; i++) {
    out.push(new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + i)).toISOString().slice(0, 10));
  }
  return out;
}

/* [{ priority, staffIds }] for each priority with 1 or 2 staff in. */
export function scarceFor(priorities, day) {
  var out = [];
  usablePriorities(priorities).forEach(function (priority) {
    var ids = staffIdsOf(day).filter(function (id) {
      return countsAsIn(id, day) && servicesCanDo(day.staff[id], priority, day).length > 0;
    });
    if (ids.length === 1 || ids.length === 2) out.push({ priority: priority, staffIds: ids });
  });
  return out;
}

/* Every booked service on the day, cancelled ones gone, in time order then id, whatever
 * order the book arrived in. */
export function bookingsOf(day) {
  var out = [];
  (day.appointments || []).forEach(function (appt) {
    if (appt.cancellation_type) return;
    (appt.parts || []).forEach(function (part) {
      var span = partSpan(appt, part);
      var startsAt = String(part.starts_at || appt.starts_at || '').replace(' ', 'T').slice(0, 19);
      out.push({
        id: appt.id + ':' + part.service_id + ':' + startsAt,
        appt: appt, part: part, staffId: part.staff_id, startsAt: startsAt,
        from: span.from, to: span.to,
      });
    });
  });
  // Each move is named by this id, on the screen and on the wire. Two services alike at
  // one minute on one appointment share it, so neither can safely be named.
  var seen = {};
  out.forEach(function (b) { seen[b.id] = (seen[b.id] || 0) + 1; });
  out.forEach(function (b) { b.ambiguous = seen[b.id] > 1; });
  return out.sort(function (a, b) {
    return (a.from - b.from) || compareStrings(a.id, b.id) || (a.staffId - b.staffId);
  });
}

/* The names of every technician the note mentions, in the order given. Any technician:
 * "Requesting to ONLY be seen by Ann" matters on a move to Tam. */
export function noteWarning(notes, staffNames) {
  if (!notes) return [];
  return (staffNames || []).filter(function (name) { return notesMention(notes, name); });
}

function lockedWhy(booking, day) {
  var part = booking.part;
  if (!requested(part)) return null;
  if (part.staff_requested_type === 'FemaleOnly') return '★ Requested female staff: stays';
  if (part.staff_requested_type === 'MaleOnly') return '★ Requested male staff: stays';
  if (part.staff_requested_type !== 'Anyone') {
    return '★ Requested ' + nameOf(day, part.staff_id, part.staff) + ': stays';
  }
  var extra = part.additional_staff_requested_type;
  if (extra === 'FemaleOnly') return '★ Requested female staff as well: stays';
  if (extra === 'MaleOnly') return '★ Requested male staff as well: stays';
  return '★ Requested ' + nameOf(day, part.additional_staff_id) + ' as well: stays';
}

function uneditableWhy(booking) {
  var appt = booking.appt, status = appt.workflow_status;
  if (booking.ambiguous) return 'The same service twice at this time can\'t be told apart: stays';
  if (status === 'Waiting' || status === 'CheckedIn') return 'Already checked in: stays';
  if (status === 'Finished' || appt.sale_id != null) return 'Already checked out: stays';
  if (status === 'NoShow') return 'Marked a no-show: stays';
  if (MOVABLE.indexOf(status) === -1) return 'Its status can\'t be read: stays';
  if (appt.repeating === true) return 'Part of a repeating series: stays';
  if (appt.can_modify === false) return 'Mangomint won\'t let this be changed: stays';
  if (booking.part.has_add_ons === true) return 'Has add-ons: stays';
  return null;
}

function receiverWhy(receiverId, booking, day, priorities) {
  var receiver = day.staff[receiverId];
  if (!receiver || !receiver.active) return 'That technician isn\'t one of this salon\'s technicians.';
  var name = receiver.name;
  if (booking.part.additional_staff_id === receiverId) return name + ' is already on this service.';
  var scarce = scarceFor(priorities, day).find(function (s) { return s.staffIds.indexOf(receiverId) !== -1; });
  if (scarce) {
    return name + ' is scarce for ' + scarce.priority.label + (day.isToday ? ' today.' : ' that day.');
  }
  if (receiver.ownClientsOnly) return name + ' is on their own clients.';
  if (day.isToday) {
    var row = day.board && day.board[receiverId];
    if (!row || !row.present) return name + ' is not on the board today.';
    if (row.skipped) {
      if (row.reason === 'break') return name + ' is on a break.';
      if (row.reason === 'done') return name + ' is done for today.';
      return name + ' is out of the rotation.';
    }
  }
  return null;
}

function clearWhy(receiverId, booking, day) {
  var name = day.staff[receiverId].name;
  var covered = spansFor(day, receiverId).some(function (s) { return s.from <= booking.from && s.to >= booking.to; });
  if (!covered) return name + ' is not on shift for all of this.';
  // What can't be read can't be shown to be clear, the same as recheckMove.
  var blocks = day.blocks[receiverId] || [];
  if (blocks.some(function (b) { return b.from == null || b.to == null; })) {
    return name + ' has a time block that can\'t be read.';
  }
  var blocked = blocks.find(function (b) { return overlaps(b, booking); });
  if (blocked) return name + ' has ' + blocked.label + ' during this.';
  var book = bookingsOf(day);
  for (var i = 0; i < book.length; i++) {
    var other = book[i];
    if (other.part === booking.part) continue;
    if (other.part.staff_id !== receiverId && other.part.additional_staff_id !== receiverId) continue;
    if (other.from == null || other.to == null) return name + ' has a booking that can\'t be read.';
    if (overlaps(other, booking)) return name + ' has a booking during this.';
  }
  if ((day.squares && day.squares[receiverId] || []).some(function (s) { return overlaps(s, booking); })) {
    return name + ' has a square running over this time.';
  }
  return null;
}

/* May this booking move to this technician? The design's *What may move*, in its order.
 *
 *   booking    from `bookingsOf`
 *   receiver   their Mangomint staff id
 *   now        wall clock, cutoffMs in milliseconds, priorities as above
 *
 * Returns { ok: true, why: null } or { ok: false, why: a sentence for the desk }.
 * Not counted here: what has already been promised, which is the planner's. */
export function candidate(booking, receiver, day, now, cutoffMs, priorities) {
  function no(why) { return { ok: false, why: why }; }
  var usable = usablePriorities(priorities);
  var locked = lockedWhy(booking, day);
  if (locked) return no(locked);
  if (isOnPriority(booking.part, usable)) return no('A priority service: stays');

  var who = receiverWhy(receiver, booking, day, usable);
  if (who) return no(who);

  var mapped = mappedServiceFor(booking.part, day);
  if (!mapped) return no('This service isn\'t mapped to a turn type: stays');
  if (!mayTake(day.staff[receiver], mapped, allowedStaffFor(booking.part, day))) {
    return no(day.staff[receiver].name + ' can\'t do this service.');
  }

  if (booking.from == null || booking.to == null) return no('This service\'s times can\'t be read: stays');
  var clear = clearWhy(receiver, booking, day);
  if (clear) return no(clear);

  var fixed = uneditableWhy(booking);
  if (fixed) return no(fixed);

  if (booking.from - now <= cutoffMs) return no('Starts too soon to move: stays');
  return { ok: true, why: null };
}

/* ---- One block ---- */

function freeStretches(walls, from, end) {
  var sorted = walls.slice().sort(function (a, b) { return a.from - b.from; });
  var out = [], at = from;
  sorted.forEach(function (w) {
    if (w.from > at) out.push({ from: at, to: Math.min(w.from, end) });
    if (w.to > at) at = w.to;
  });
  if (at < end) out.push({ from: at, to: end });
  return out.filter(function (g) { return g.to > g.from; });
}

/* Everything in the way on a technician's day, on the real timeline, besides the
 * bookings that might move. Null when any of it can't be read: an unknown is not a gap. */
function fixedWalls(staffId, bookings, day, line) {
  var walls = [];
  var readable = true;
  function add(from, to) {
    if (from == null || to == null) { readable = false; return; }
    walls.push({ from: line.real(from), to: line.real(to) });
  }
  (day.blocks[staffId] || []).forEach(function (b) { add(b.from, b.to); });
  ((day.squares && day.squares[staffId]) || []).forEach(function (s) { add(s.from, s.to); });
  bookings.forEach(function (b) { add(b.from, b.to); });
  var spans = spansFor(day, staffId);
  for (var i = 1; i < spans.length; i++) add(spans[i - 1].to, spans[i].from);
  return readable ? walls : null;
}

function buildMoves(staffId, movable, day, now, cutoffMs, usable, promised, seed) {
  var assigned = [];
  var tentative = [];
  movable.forEach(function (booking) {
    var eligible = staffIdsOf(day).filter(function (id) {
      if (id === staffId || !candidate(booking, id, day, now, cutoffMs, usable).ok) return false;
      return !promised.concat(tentative).some(function (p) { return p.staffId === id && overlaps(p, booking); });
    });
    if (!eligible.length) return;
    var taking = assigned.filter(function (m) { return m.booking.appt === booking.appt; })
      .map(function (m) { return m.to; });
    var keepsSquare = eligible.filter(function (id) { return taking.indexOf(id) !== -1; });
    var pool = keepsSquare.length ? keepsSquare : eligible;
    var pick = pool.slice().sort(function (a, b) {
      return hash(seed + '|' + booking.id + '|' + a) - hash(seed + '|' + booking.id + '|' + b) || a - b;
    })[0];
    assigned.push({ booking: booking, to: pick });
    tentative.push({ id: booking.id, staffId: pick, from: booking.from, to: booking.to });
  });
  return assigned;
}

/* The sentence on a move that splits a visit, or null. Another service of this visit on
 * the same technician, ending up with someone else. `toFor(part)` is who that part is
 * moving to, or null if it stays. */
export function splitWhy(booking, to, staffId, day, toFor) {
  var left = (booking.appt.parts || []).filter(function (p) { return p !== booking.part && p.staff_id === staffId; })
    .map(function (p) {
      var moved = toFor(p);
      return moved == null ? staffId : moved;
    }).find(function (id) { return id !== to; });
  if (left == null) return null;
  return 'Just this service: the rest of the visit ' +
    (left === staffId ? 'stays with ' : 'goes to ') + nameOf(day, left) + ', so one square can become two.';
}

function moveLine(move, staffId, day, assigned) {
  var b = move.booking;
  var notes = (day.notes && b.appt.client && day.notes[b.appt.client.id]) || null;
  var names = staffIdsOf(day).map(function (id) { return day.staff[id].name; });
  var warn = noteWarning(notes, names);
  var why = splitWhy(b, move.to, staffId, day, function (p) {
    var m = assigned.find(function (x) { return x.booking.part === p; });
    return m ? m.to : null;
  });
  return makeLine(b, 'move', day, { ticked: warn.length === 0, to: move.to, why: why, warn: warn, notes: notes });
}

function makeLine(b, kind, day, over) {
  var client = b.appt.client || {};
  return Object.assign({
    id: b.id,
    kind: kind,
    ticked: false,
    to: null,
    why: null,
    warn: [],
    notes: null,
    appointment_id: b.appt.id,
    service_id: b.part.service_id,
    starts_at: b.startsAt,
    from_staff_id: b.part.staff_id,
    service: b.part.service || null,
    clientName: [client.first_name, client.last_name].filter(Boolean).join(' '),
    staffName: nameOf(day, b.part.staff_id, b.part.staff),
    fromMs: b.from,
    toMs: b.to,
  }, over, {
    to_staff_id: over.to == null ? null : over.to,
    toName: over.to == null ? null : day.staff[over.to].name,
  });
}

/* A block for this technician and priority, or null. `taken` is the bookings no block
 * may offer because a block already has them, and `dismissedSet` those the desk said no to. */
function planBlock(staffId, priority, seed, day, o, usable, taken, dismissedSet) {
  var can = servicesCanDo(day.staff[staffId], priority, day);
  var need = Math.min.apply(null, can.map(function (s) { return s.minutes; })) * MINUTE;
  if (!Number.isFinite(need)) return null;

  var line = timeline(day.tz);
  var spans = spansFor(day, staffId);
  if (!spans.length) return null;
  var book = bookingsOf(day);
  var mine = book.filter(function (b) { return b.staffId === staffId; });
  var second = book.filter(function (b) { return b.part.additional_staff_id === staffId; });

  function mayOffer(b) {
    return b.from != null && b.to != null && !taken[b.id] && !dismissedSet[bookingKey(b.id, b.part.staff_id, b.from, b.to)] && !lockedWhy(b, day) && !uneditableWhy(b) &&
      !isOnPriority(b.part, usable) && mappedServiceFor(b.part, day) && b.from - o.now > o.cutoffMs;
  }
  var offered = mine.filter(mayOffer);
  var fixedBook = mine.filter(function (b) { return !mayOffer(b); }).concat(second);
  var fixed = fixedWalls(staffId, fixedBook, day, line);
  if (!fixed) return null;

  var start = line.real(Math.max(o.now, spans[0].from));
  var end = line.real(spans[spans.length - 1].to);
  function realSpan(b) { return { from: line.real(b.from), to: line.real(b.to) }; }

  // Offer everything, then take back what opens nothing usable and offer again, because
  // a colleague freed from a pointless promise may be just what another booking needs.
  var assigned = [];
  var remaining = offered.slice();
  for (;;) {
    assigned = buildMoves(staffId, remaining, day, o.now, o.cutoffMs, usable, o.promised, seed);
    var movedParts = assigned.map(function (m) { return m.booking; });
    var left = offered.filter(function (b) { return movedParts.indexOf(b) === -1; }).map(realSpan);
    var free = freeStretches(fixed.concat(left), start, end);
    var useless = movedParts.filter(function (b) {
      var s = realSpan(b);
      return !free.some(function (g) { return g.from <= s.from && g.to >= s.to && g.to - g.from >= need; });
    });
    if (!useless.length) break;
    remaining = remaining.filter(function (b) { return useless.indexOf(b) === -1; });
  }
  if (!assigned.length) return null;

  var moved = assigned.map(function (m) { return m.booking; });
  // Plain numbers within the day, so the block survives being stored as JSON.
  function clamp(w) { return { from: Math.max(w.from, start), to: Math.min(w.to, end) }; }
  var basis = {
    start: start, end: end, needMs: need, tz: day.tz || null,
    walls: fixed.concat(offered.filter(function (b) { return moved.indexOf(b) === -1; }).map(realSpan))
      .map(clamp).filter(function (w) { return w.to > w.from; })
      .sort(function (x, y) { return (x.from - y.from) || (x.to - y.to); }),
    moves: moved.map(function (b) { return Object.assign({ id: b.id }, clamp(realSpan(b))); }),
  };

  var stretch = {
    from: Math.min.apply(null, moved.map(function (b) { return b.from; })),
    to: Math.max.apply(null, moved.map(function (b) { return b.to; })),
  };
  var lines = assigned.map(function (m) { return moveLine(m, staffId, day, assigned); });
  mine.concat(second).forEach(function (b) {
    if (b.from == null || b.to == null || b.from > stretch.to || b.to < stretch.from) return;
    var isSecond = b.staffId !== staffId;
    var locked = isSecond ? day.staff[staffId].name + ' is the second technician on this: stays' : lockedWhy(b, day);
    var fixedWhy = locked ? null : uneditableWhy(b);
    if (locked) lines.push(makeLine(b, 'locked', day, { why: locked }));
    else if (fixedWhy) lines.push(makeLine(b, 'uneditable', day, { why: fixedWhy }));
  });
  lines.sort(function (a, b) {
    return (a.fromMs - b.fromMs) || compareStrings(a.id, b.id) || (a.from_staff_id - b.from_staff_id);
  });

  var block = {
    date: day.date,
    staffId: staffId,
    staffName: day.staff[staffId].name,
    priority: { id: priority.id, label: priority.label, kind: priority.kind, minutes: need / MINUTE },
    seed: seed,
    basis: basis,
    lines: lines,
  };
  block.gain = gainFor(block, basis.moves.map(function (m) { return m.id; }));
  block.key = stableKey(block);
  return block;
}

/* What the block gains if only these lines are approved: { minutes, earliestFrom,
 * earliestTo }. Everything unticked stays in the way. The "from" side is always the
 * book as written, and with nothing ticked `earliestTo` equals it and `minutes` is 0. */
export function gainFor(block, tickedIds) {
  var b = block.basis;
  var line = timeline(b.tz);
  var ticked = tickedIds || [];
  var asBooked = b.walls.concat(b.moves);
  var stays = b.walls.concat(b.moves.filter(function (m) { return ticked.indexOf(m.id) === -1; }));
  function freeMs(walls) {
    return freeStretches(walls, b.start, b.end).reduce(function (n, g) { return n + (g.to - g.from); }, 0);
  }
  function earliest(walls) {
    var at = firstGap(b.start, b.needMs, walls.concat([{ from: b.end, to: Infinity }]));
    return Number.isFinite(at) ? line.wall(at) : null;
  }
  return {
    minutes: Math.round((freeMs(stays) - freeMs(asBooked)) / MINUTE),
    earliestFrom: earliest(asBooked),
    earliestTo: earliest(stays),
  };
}

function bookingKey(id, staffId, from, to) {
  return hex(hash([id, staffId, from, to].join('|')));
}

/* What goes on the `dismissed` list when the desk dismisses a block (every move on it)
 * or one line. */
export function dismissalKeys(blockOrLine) {
  var lines = blockOrLine.lines || [blockOrLine];
  return lines.filter(function (l) { return l.kind === 'move'; }).map(function (l) {
    return bookingKey(l.id, l.from_staff_id, l.fromMs, l.toMs);
  });
}

/* A hash of the bookings on a block, naming its stored row until they
 * change. Not the seed, not who receives and not whether a line is ticked. In any
 * order. */
export function stableKey(block) {
  var parts = (block.lines || []).map(function (l) {
    return [l.id, l.kind, l.from_staff_id, l.fromMs, l.toMs].join('|');
  }).sort();
  var text = [block.date, block.staffId, block.priority && block.priority.id].concat(parts).join('\n');
  return hex(hash(text));
}

/* The day's proposals.
 *
 *   opts   priorities, now (wall clock; the day's own if left out), cutoffMs (30 minutes
 *          if left out), seed, dismissed (`dismissalKeys`), promised (added to)
 *
 * Returns { date, highlight: [{ priorityId, label, count, staffIds, names }], blocks }. */
export function planDay(day, opts) {
  var o = Object.assign({}, opts);
  o.now = o.now == null ? day.now : o.now;
  o.cutoffMs = o.cutoffMs == null ? 30 * MINUTE : o.cutoffMs;
  o.promised = o.promised || [];
  var dismissedSet = {};
  (o.dismissed || []).forEach(function (k) { dismissedSet[k] = true; });
  var usable = usablePriorities(o.priorities);
  var scarce = scarceFor(usable, day);

  var highlight = scarce.map(function (s) {
    return {
      priorityId: s.priority.id,
      label: s.priority.label,
      count: s.staffIds.length,
      staffIds: s.staffIds,
      names: s.staffIds.map(function (id) { return day.staff[id].name; }),
    };
  }).sort(function (a, b) { return (a.count - b.count) || compareStrings(a.label, b.label); });

  // Which scarce technician to work on first is a tie, so it is the seed's to break.
  var work = [];
  scarce.forEach(function (s) {
    s.staffIds.forEach(function (staffId) {
      var seed = hex(hash(o.seed + '|' + day.date + '|' + staffId + '|' + s.priority.id));
      work.push({ staffId: staffId, priority: s.priority, seed: seed });
    });
  });
  work.sort(function (a, b) { return compareStrings(a.seed, b.seed); });

  var taken = {};
  o.promised.forEach(function (p) { taken[p.id] = true; });
  var blocks = [];
  work.forEach(function (w) {
    var block = planBlock(w.staffId, w.priority, w.seed, day, o, usable, taken, dismissedSet);
    if (!block) return;
    var moves = block.lines.filter(function (l) { return l.kind === 'move'; });
    moves.forEach(function (l) { taken[l.id] = true; });
    moves.forEach(function (l) {
      o.promised.push({ id: l.id, staffId: l.to, from: l.fromMs, to: l.toMs });
    });
    blocks.push(block);
  });

  return { date: day.date, highlight: highlight, blocks: blocks };
}

/* planDay for each date, in date order, with one list of promises across them all. */
export function planHorizon(days, opts) {
  var o = Object.assign({}, opts);
  o.promised = o.promised || [];
  return (days || []).slice().sort(function (a, b) { return compareStrings(a.date, b.date); })
    .map(function (day) { return planDay(day, o); });
}

/* The day's highlight as the design writes it: "Only 1 in today: Facials (Helen). Only
 * 2 in: Dip Powder." "Today" goes on the first sentence, and only on today. */
export function highlightText(highlight, isToday) {
  var sentences = [];
  [1, 2].forEach(function (count) {
    var these = (highlight || []).filter(function (h) { return h.count === count; });
    if (!these.length) return;
    var labels = these.map(function (h) {
      return count === 1 ? h.label + ' (' + h.names[0] + ')' : h.label;
    });
    var word = isToday && !sentences.length ? ' today' : '';
    sentences.push('Only ' + count + ' in' + word + ': ' + labels.join(', ') + '.');
  });
  return sentences.join(' ');
}
